import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { DictateButton } from '../components/DictateButton';
import { useUserOrg } from '../lib/org';
import {
  getSite,
  createForm,
  setFormSignaturePath,
  createFormAttendee,
  listActiveSignIns,
  type Site,
  type SignIn,
} from '../lib/db';
import { uploadSignature } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { enqueue as enqueueOutbox, blobToDataUrl } from '../lib/outbox';

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}
import { Modal } from '../components/Modal';
import { POPULAR_TBT_TEMPLATES, type ToolboxTalkTemplate } from '../data/toolboxTemplates';
import { LoadingIndicator } from '../components/LoadingIndicator';

interface AttendeeDraft {
  name: string;
  company: string;
  verbal_only?: boolean;
}

export function FormTBT() {
  const { siteId } = useParams<{ siteId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const formIdRef = useRef<string>(crypto.randomUUID());
  const sigRef = useRef<SignaturePadHandle>(null);
  const attendeeSigRefs = useRef<Map<number, SignaturePadHandle | null>>(new Map());

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [topic, setTopic] = useState('');
  const [why, setWhy] = useState('');
  const [byName, setByName] = useState('');
  const [mins, setMins] = useState('');
  const [points, setPoints] = useState('');
  const [actions, setActions] = useState('');
  const [attendees, setAttendees] = useState<AttendeeDraft[]>([{ name: '', company: '' }]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [active, setActive] = useState<SignIn[]>([]);

  useEffect(() => {
    if (!siteId) return;
    Promise.all([getSite(siteId), listActiveSignIns(siteId).catch(() => [])])
      .then(([s, a]) => { setSite(s); setActive(a); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      if (name && !byName) setByName(name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  function updateAttendee(i: number, patch: Partial<AttendeeDraft>) {
    setAttendees((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }

  function addFromActive(p: SignIn) {
    setAttendees((prev) => {
      // If a row with matching name+company exists already, do nothing.
      const exists = prev.some((a) => a.name.trim().toLowerCase() === p.full_name.trim().toLowerCase() && a.company.trim().toLowerCase() === p.company.trim().toLowerCase());
      if (exists) return prev;
      // If the first row is empty, fill it; otherwise append.
      const filled = { name: p.full_name, company: p.company };
      if (prev.length === 1 && !prev[0].name.trim() && !prev[0].company.trim()) return [filled];
      return [...prev, filled];
    });
  }

  function addAllActive() {
    setAttendees((prev) => {
      const startBlank = prev.length === 1 && !prev[0].name.trim() && !prev[0].company.trim();
      const out = startBlank ? [] : prev.slice();
      for (const p of active) {
        const exists = out.some((a) => a.name.trim().toLowerCase() === p.full_name.trim().toLowerCase() && a.company.trim().toLowerCase() === p.company.trim().toLowerCase());
        if (!exists) out.push({ name: p.full_name, company: p.company });
      }
      return out.length === 0 ? [{ name: '', company: '' }] : out;
    });
  }

  function addAttendee() {
    setAttendees((prev) => [...prev, { name: '', company: '', verbal_only: false }]);
  }

  function removeAttendee(i: number) {
    attendeeSigRefs.current.delete(i);
    setAttendees((prev) => prev.filter((_, idx) => idx !== i));
  }

  function applyTemplate(t: ToolboxTalkTemplate) {
    setTopic(t.topic);
    setWhy(t.why);
    setPoints(t.points);
    setMins(String(t.duration_mins));
    setShowTemplates(false);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!org || !site) return;
    setError(null);

    if (!topic.trim() || !byName.trim()) {
      setError('Topic and Delivered by are required.');
      return;
    }
    const filledAttendees = attendees.filter((a) => a.name.trim());
    if (filledAttendees.length === 0) {
      setError('Add at least one attendee.');
      return;
    }

    setSubmitting(true);
    try {
      const formId = formIdRef.current;
      const sigBlob = sigRef.current?.isEmpty() ? null : await sigRef.current!.toBlob();

      // Pre-collect attendee signature blobs
      const attendeeBlobs: { idx: number; blob: Blob | null }[] = [];
      for (let i = 0; i < attendees.length; i++) {
        if (!attendees[i].name.trim()) continue;
        const handle = attendeeSigRefs.current.get(i);
        const blob = handle?.isEmpty() ? null : await handle?.toBlob() ?? null;
        attendeeBlobs.push({ idx: i, blob });
      }

      // Offline path — queue the whole TBT (form + reporter signature + N
      // attendees each with optional signature). The dispatcher replays in
      // the same order as the online path below.
      if (!isOnline()) {
        const sigDataUrl = sigBlob ? await blobToDataUrl(sigBlob) : null;
        const offlineAttendees: Array<{
          full_name: string;
          company: string | null;
          verbal_only: boolean;
          signature_data_url?: string | null;
        }> = [];
        for (const { idx, blob } of attendeeBlobs) {
          const a = attendees[idx];
          offlineAttendees.push({
            full_name: a.name.trim(),
            company: a.company.trim() || null,
            verbal_only: !!a.verbal_only,
            signature_data_url: (!a.verbal_only && blob) ? await blobToDataUrl(blob) : null,
          });
        }
        await enqueueOutbox('form.tbt', org.org_id, {
          formId,
          orgId: org.org_id,
          siteId: site.id,
          kind: 'toolbox_talk',
          title: `Toolbox talk: ${topic.trim()}`,
          performed_by_name: byName.trim(),
          data: {
            topic: topic.trim(),
            why: why.trim(),
            mins: mins.trim(),
            points: points.trim(),
            actions: actions.trim(),
          },
          attendee_count: filledAttendees.length,
          signature_data_url: sigDataUrl,
          attendees: offlineAttendees,
        });
        nav(`/sites/${site.id}/forms`);
        return;
      }

      await createForm({
        id: formId,
        orgId: org.org_id,
        siteId: site.id,
        kind: 'toolbox_talk',
        title: `Toolbox talk: ${topic.trim()}`,
        performed_by_name: byName.trim(),
        data: {
          topic: topic.trim(),
          why: why.trim(),
          mins: mins.trim(),
          points: points.trim(),
          actions: actions.trim(),
        },
        attendee_count: filledAttendees.length,
      });

      if (sigBlob) {
        const path = await uploadSignature({
          orgId: org.org_id,
          signInId: `form-${formId}`,
          side: 'in',
          blob: sigBlob,
        });
        await setFormSignaturePath(formId, path);
      }

      // Insert attendee rows + upload their signatures (if not verbal-only)
      for (const { idx, blob } of attendeeBlobs) {
        const a = attendees[idx];
        let path: string | null = null;
        if (!a.verbal_only && blob) {
          path = `${org.org_id}/form-${formId}/attendee-${idx}.png`;
          const { error: upErr } = await supabase.storage
            .from('signatures')
            .upload(path, blob, { contentType: 'image/png', upsert: true });
          if (upErr) throw upErr;
        }
        await createFormAttendee({
          form_id: formId,
          full_name: a.name.trim(),
          company: a.company.trim() || null,
          signature_url: path,
          verbal_only: !!a.verbal_only,
        });
      }

      nav(`/sites/${site.id}/forms`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) {
    return <AppShell title="Toolbox talk" back={`/sites/${siteId ?? ''}/forms`}><LoadingIndicator /></AppShell>;
  }
  if (!site || !org) {
    return <AppShell title="Toolbox talk" back={`/sites/${siteId ?? ''}/forms`}><div className="banner banner-error">{error ?? 'Site not found.'}</div></AppShell>;
  }

  return (
    <AppShell title="Toolbox talk" back={`/sites/${site.id}/forms`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}>Record what you talked about and capture each attendee's signature.</p>
      </div>

      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => setShowTemplates(true)}
        style={{ marginBottom: 12 }}
      >
        ☰ Pick from {POPULAR_TBT_TEMPLATES.length} popular topics
      </button>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="t-topic">Topic *</label>
          <div className="input-with-mic">
            <input id="t-topic" type="text" required placeholder="e.g. Silica dust — wet cutting" value={topic} onChange={(e) => setTopic(e.target.value)} />
            <DictateButton onTranscript={(t) => setTopic((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="t-why">Why this topic now?</label>
          <div className="input-with-mic">
            <textarea id="t-why" rows={2} placeholder="Linked to RAMS / season / recent near-miss…" value={why} onChange={(e) => setWhy(e.target.value)} />
            <DictateButton onTranscript={(t) => setWhy((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="t-by">Delivered by *</label>
            <input id="t-by" type="text" required value={byName} onChange={(e) => setByName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="t-mins">Duration (mins)</label>
            <input id="t-mins" type="number" placeholder="10" value={mins} onChange={(e) => setMins(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="t-points">Key points covered</label>
          <div className="input-with-mic">
            <textarea id="t-points" rows={3} value={points} onChange={(e) => setPoints(e.target.value)} />
            <DictateButton onTranscript={(t) => setPoints((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="t-actions">Questions / actions agreed</label>
          <div className="input-with-mic">
            <textarea id="t-actions" rows={2} value={actions} onChange={(e) => setActions(e.target.value)} />
            <DictateButton onTranscript={(t) => setActions((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Attendees</h3>
        <p className="muted" style={{ margin: '0 0 12px' }}>Each attendee signs to acknowledge they attended and understood.</p>

        {active.length > 0 && (
          <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)', borderColor: 'var(--brand-light)' }}>
            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
              {active.length} {active.length === 1 ? 'person is' : 'people are'} on site right now
            </p>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
              Tap each person who attended this talk — or add them all at once.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {active.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="recent-op-chip"
                  onClick={() => addFromActive(p)}
                >
                  + <strong>{p.full_name}</strong>
                  <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>{p.company}</span>
                </button>
              ))}
            </div>
            <button type="button" className="btn-link-sm" onClick={addAllActive}>
              Add everyone on site
            </button>
          </div>
        )}

        {attendees.map((a, i) => (
          <div className="card" key={i} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => removeAttendee(i)}
              aria-label="Remove attendee"
              style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', color: 'var(--ink-soft)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
            >×</button>
            <div className="field-row">
              <div className="field">
                <label htmlFor={`tbt-att-name-${i}`}>Name</label>
                <input id={`tbt-att-name-${i}`} type="text" value={a.name} onChange={(e) => updateAttendee(i, { name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor={`tbt-att-company-${i}`}>Company</label>
                <input id={`tbt-att-company-${i}`} type="text" value={a.company} onChange={(e) => updateAttendee(i, { company: e.target.value })} />
              </div>
            </div>
            <label className="check-row" style={{ marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={!!a.verbal_only}
                onChange={(e) => {
                  updateAttendee(i, { verbal_only: e.target.checked });
                  if (e.target.checked) {
                    // If switching to verbal, clear any signature drawn
                    const h = attendeeSigRefs.current.get(i);
                    h?.clear?.();
                  }
                }}
              />
              <span>Verbal acknowledgement only — didn't sign</span>
            </label>
            {!a.verbal_only && (
              <div className="field">
                <label>Signature</label>
                <SignaturePad
                  ref={(h) => {
                    if (h) attendeeSigRefs.current.set(i, h);
                    else attendeeSigRefs.current.delete(i);
                  }}
                  label="Sign to confirm attended"
                  height={120}
                />
              </div>
            )}
          </div>
        ))}

        <button type="button" className="btn btn-ghost" onClick={addAttendee}>+ Add attendee</button>

        <h3 className="section-title" style={{ marginTop: 22 }}>Your signature (optional)</h3>
        <SignaturePad ref={sigRef} label="Signed by deliverer" />

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save toolbox talk'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}/forms`)}>Cancel</button>
        </div>
      </form>

      <Modal open={showTemplates} title="Pick a topic" onClose={() => setShowTemplates(false)}>
        <p className="muted" style={{ margin: '0 0 12px' }}>
          Tap one to fill in the form. You can still edit every field before saving.
        </p>
        <ul className="tbt-template-list">
          {POPULAR_TBT_TEMPLATES.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className="tbt-template-row"
                onClick={() => applyTemplate(t)}
              >
                <div>
                  <p className="tbt-template-topic">{t.topic}</p>
                  <p className="tbt-template-why">{t.why}</p>
                </div>
                <span className="tbt-template-mins">{t.duration_mins}m</span>
              </button>
            </li>
          ))}
        </ul>
        <p className="muted" style={{ margin: '12px 0 0', fontSize: 13 }}>
          Need something not on this list? Close this and type your own topic.
        </p>
      </Modal>
    </AppShell>
  );
}
